import * as React from "react";
import { mergeClasses } from "@fluentui/react-components";
import { IconPhone, useFormPrimitiveStyles } from "../formPrimitives";
import { PLACEHOLDERS } from "../validation";
import { useTelephony } from "../../../context/TelephonyContext";
import { ErreichbarkeitClockButton } from "./ErreichbarkeitClockButton";
import { AnsichtsKnopf } from "../AnsichtsKnopf";
import { aktionsKnopfStil } from "./aktionsKnopfStil";
import { useTelefonFeld } from "./useTelefonFeld";
import type { TelefonOptionen } from "@kmvs/forms-core";
import { SperrWert } from "../SperrWert";

const ErrIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
    <circle cx="8" cy="8" r="6.4" />
    <path d="M8 4.8v3.6" strokeLinecap="round" />
    <circle cx="8" cy="11.1" r="0.5" fill="currentColor" stroke="none" />
  </svg>
);

/**
 * Telefon-/Fax-Feld im Format „+49 221 123456-78" (siehe useTelefonFeld):
 * immer mit „+", Ortsvorwahl nach der BNetzA-Liste getrennt, Durchwahl nur
 * bei geschäftlichen Nummern.
 */
export function PhoneInput({
  label,
  name,
  value,
  onChange,
  required,
  rightSlot,
  kind = "mobile",
  actionButton = true,
  leftIcon,
  erreichbarkeit,
  onErreichbarkeitChange,
  onRemoveIfEmpty,
  labelRightSlot: customLabelRightSlot,
  fieldLabelText,
  geschaeftlich,
  zentrale,
  unterFeld,
}: {
  label: React.ReactNode;
  name: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  /** Knöpfe rechts neben dem Feld (z. B. Anrufen, Entfernen). */
  rightSlot?: React.ReactNode;
  kind?: "mobile" | "landline" | "fax";
  actionButton?: boolean;
  leftIcon?: React.ReactNode;
  erreichbarkeit?: string;
  onErreichbarkeitChange?: (v: string) => void;
  onRemoveIfEmpty?: () => void;
  labelRightSlot?: React.ReactNode;
  fieldLabelText?: string;
  /** Geschäftliche Nummer (Firma, Kommunikationsweg „geschäftlich"): Durchwahl „-" erlaubt. */
  geschaeftlich?: boolean;
  /** Zentralnummer(n) der Firma — eine längere Nummer mit gleichem Anfang bekommt die Durchwahl. */
  zentrale?: TelefonOptionen["zentrale"];
  /** Zusatz unter dem Feld (z. B. der Hinweis „Das ist eine Mobilnummer."). */
  unterFeld?: React.ReactNode;
}) {
  const s = useFormPrimitiveStyles();
  const [touched, setTouched] = React.useState(false);
  const feld = useTelefonFeld({
    value,
    onChange,
    geschaeftlich,
    zentrale,
    onBlur: (committedVal) => {
      setTouched(true);
      if (onRemoveIfEmpty && !committedVal.trim()) onRemoveIfEmpty();
    },
  });

  let callNumber: ((num: string) => void) | null = null;
  try {
    const telCtx = useTelephony();
    callNumber = telCtx.callNumber;
  } catch {
    callNumber = null;
  }

  const placeholder =
    kind === "fax" ? PLACEHOLDERS.fax : kind === "landline" ? PLACEHOLDERS.phoneLandline : PLACEHOLDERS.phoneMobile;

  const hasValue = Boolean(feld.wert);

  const handleCall = () => {
    if (!hasValue) return;
    const cleanNum = feld.wert.replace(/[\s-]+/g, "");
    if (callNumber) {
      try {
        callNumber(cleanNum);
        return;
      } catch {
        // Fallback
      }
    }
    window.location.href = `tel:${cleanNum}`;
  };

  // Kein <button>: Anrufen ändert nichts und soll auch bei geschlossenem
  // Schloss der Akte (`<fieldset disabled>`) gehen (User 30.09.2026).
  const callBtn = actionButton && kind !== "fax" ? (
    <AnsichtsKnopf
      label={hasValue ? `Anrufen: ${feld.wert}` : "Anrufen"}
      title={hasValue ? `Anrufen: ${feld.wert}` : "Bitte Rufnummer eingeben"}
      deaktiviert={!hasValue}
      onClick={handleCall}
      className={s.aktionsKnopf}
      style={aktionsKnopfStil(hasValue)}
    >
      <IconPhone />
    </AnsichtsKnopf>
  ) : undefined;

  const plainLabel =
    fieldLabelText ||
    (typeof label === "string"
      ? label
      : kind === "landline"
      ? "Telefon"
      : kind === "fax"
      ? "Fax"
      : "Mobil");

  const clockSlot =
    hasValue && onErreichbarkeitChange ? (
      <ErreichbarkeitClockButton
        fieldLabel={plainLabel}
        value={erreichbarkeit}
        onChange={onErreichbarkeitChange}
      />
    ) : null;

  const combinedLabelRightSlot =
    customLabelRightSlot || clockSlot ? (
      <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
        {customLabelRightSlot}
        {clockSlot}
      </div>
    ) : null;

  const fehler = touched ? feld.fehler : undefined;
  const icon = leftIcon ?? (rightSlot ? <IconPhone /> : undefined);
  const inputEl = (
    <div className={s.inputWrap}>
      {icon && <span className={s.inputIcon}>{icon}</span>}
      <input
        {...feld.inputProps}
        id={name}
        name={name}
        className={mergeClasses(s.input, !!icon && s.inputWithIcon, !!fehler && s.inputInvalid)}
        placeholder={placeholder}
        aria-invalid={fehler ? true : undefined}
      />
      <SperrWert />
    </div>
  );
  // Eigene Knöpfe (Kommunikationskarte der Firma) ersetzen den Anruf-Knopf.
  const slot = rightSlot ?? callBtn;

  return (
    <div className={s.field}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <label className={s.label} htmlFor={name}>
          {label}
          {required && <span className={s.req}> *</span>}
        </label>
        {combinedLabelRightSlot}
      </div>
      {slot ? (
        <div className={s.fieldWithSlot}>
          {inputEl}
          {slot}
        </div>
      ) : (
        inputEl
      )}
      {fehler && (
        <div className={s.errorMsg}>
          <ErrIcon />
          {fehler}
        </div>
      )}
      {unterFeld}
    </div>
  );
}

export default PhoneInput;

